import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getActiveTradeGroupsDetails } from '../api/tradeGroups';
import { getDailyProfit } from '../api/profit';
import { getPerformanceRollups } from '../api/performanceRollups';
import './PerformanceAnalytics.css';
import './shared/MetricCards.css';
import DashboardBottomLogo from './DashboardBottomLogo';
import { TrendingUpIcon } from './shared/Icons';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import ProductPerformanceChart from './charts/ProductPerformanceChart';
import StrategyComparisonChart from './charts/StrategyComparisonChart';
import ProfitTrendChart from './charts/ProfitTrendChart';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';

const PerformanceAnalytics: React.FC = () => {
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('analytics');

  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('analytics');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, invalidate all queries to refetch data
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries();
    }
  }, [refreshTrigger, queryClient]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['active-trade-groups-details'],
    queryFn: getActiveTradeGroupsDetails,
    retry: 2,
  });

  // Measured (fee-true) completed-trade count, all-time, across strategies (MPL-003 REQ-015).
  // Replaces the prior client-side estimate (Math.round(totalCapturedProfit / (avgCapturedReturn * 100))),
  // which fabricated a trade count from unrelated group-level aggregates rather than counting actual trades.
  const {
    data: measuredRollups,
    isLoading: measuredTradesLoading,
    isError: measuredTradesError,
  } = useQuery({
    queryKey: ['performance-rollups', 'strategy', refreshTrigger],
    queryFn: () => getPerformanceRollups({ groupBy: 'strategy' }),
    retry: 1,
  });

  const measuredCompletedTrades = measuredRollups
    ? measuredRollups
        .filter(row => !row.coverageGap)
        .reduce((sum, row) => sum + row.tradeCount, 0)
    : null;

  // Fetch real historical profit data for the last 30 days
  const getLast30Days = () => {
    const dates = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const dates = getLast30Days();

  // Fetch daily profit data for each of the last 30 days
  const dailyProfitQueries = dates.map(date =>
    useQuery({
      queryKey: ['daily-profit', date],
      queryFn: () => getDailyProfit(date),
      staleTime: 300000, // 5 minutes
      retry: 1,
    })
  );

  // Calculate win rate and other metrics from trade group data
  const calculateMetrics = () => {
    if (!data?.tradeGroups) {
      return {
        winRate: 0,
        avgTradeReturn: 0,
        totalProfit: 0,
        feeEfficiency: 0,
        activeGroups: 0,
      };
    }

    const groups = data.tradeGroups;

    const groupsWithProfit = groups.filter(g => g.totalCapturedProfit > 0);
    const winRate = groups.length > 0 ? (groupsWithProfit.length / groups.length) * 100 : 0;

    const avgTradeReturn = groups.reduce((sum, g) => sum + (g.avgCapturedReturn || 0), 0) / groups.length || 0;
    const totalProfit = groups.reduce((sum, g) => sum + g.totalCapturedProfit, 0);
    const totalFees = groups.reduce((sum, g) => sum + g.totalFees, 0);
    const feeEfficiency = totalProfit > 0 ? (totalFees / totalProfit) * 100 : 0;

    return {
      winRate,
      avgTradeReturn: avgTradeReturn * 100, // Convert to percentage
      totalProfit,
      feeEfficiency,
      activeGroups: groups.length,
    };
  };

  // Prepare product performance data
  const getProductPerformanceData = () => {
    if (!data?.tradeGroups) return [];

    const productMap = new Map<string, { profit: number; tradeCount: number; returns: number[] }>();

    data.tradeGroups.forEach(group => {
      if (!productMap.has(group.productId)) {
        productMap.set(group.productId, { profit: 0, tradeCount: 0, returns: [] });
      }
      const product = productMap.get(group.productId)!;
      product.profit += group.totalCapturedProfit;
      product.tradeCount += 1;
      if (group.avgCapturedReturn) {
        product.returns.push(group.avgCapturedReturn);
      }
    });

    return Array.from(productMap.entries()).map(([productId, data]) => ({
      productId,
      profit: data.profit,
      tradeCount: data.tradeCount,
      avgReturn: data.returns.length > 0
        ? data.returns.reduce((sum, r) => sum + r, 0) / data.returns.length
        : 0
    }));
  };

  // Prepare strategy comparison data
  const getStrategyComparisonData = () => {
    if (!data?.tradeGroups) return [];

    const strategyMap = new Map<string, { profit: number; returns: number[]; groups: number; costBasis: number }>();

    data.tradeGroups.forEach(group => {
      if (!strategyMap.has(group.tradeGroupType)) {
        strategyMap.set(group.tradeGroupType, { profit: 0, returns: [], groups: 0, costBasis: 0 });
      }
      const strategy = strategyMap.get(group.tradeGroupType)!;
      strategy.profit += group.totalCapturedProfit;
      strategy.groups += 1;
      strategy.costBasis += group.openSellCostBasis + group.openBuyCostBasis;
      if (group.avgCapturedReturn) {
        strategy.returns.push(group.avgCapturedReturn);
      }
    });

    return Array.from(strategyMap.entries()).map(([strategyType, data]) => ({
      strategyType,
      totalProfit: data.profit,
      avgReturn: data.returns.length > 0
        ? data.returns.reduce((sum, r) => sum + r, 0) / data.returns.length
        : 0,
      activeGroups: data.groups,
      costBasis: data.costBasis
    }));
  };

  // Get REAL profit trend data from historical daily profit records
  const getProfitTrendData = () => {
    // Check if all daily profit queries have loaded
    const allLoaded = dailyProfitQueries.every(q => !q.isLoading);
    const anyError = dailyProfitQueries.some(q => q.isError);

    if (!allLoaded || anyError) {
      return [];
    }

    const trendData: { date: string; profit: number; cumulativeProfit: number; }[] = [];
    let cumulativeProfit = 0;

    dailyProfitQueries.forEach((query, index) => {
      const date = dates[index];
      const dailyData = query.data;

      // Handle both array response and single object response
      let dayProfit = 0;
      if (dailyData) {
        if (Array.isArray(dailyData)) {
          // Sum all products for this day
          dayProfit = dailyData.reduce((sum, item) => sum + (item.net_profit || 0), 0);
        } else {
          // Single object response
          dayProfit = dailyData.net_profit || 0;
        }
      }

      cumulativeProfit += dayProfit;

      trendData.push({
        date,
        profit: dayProfit,
        cumulativeProfit: cumulativeProfit
      });
    });

    return trendData;
  };

  // Check if profit trend queries are still loading
  const profitTrendLoading = dailyProfitQueries.some(q => q.isLoading);
  const profitTrendError = dailyProfitQueries.some(q => q.isError);

  const metrics = calculateMetrics();
  const productData = getProductPerformanceData();
  const strategyData = getStrategyComparisonData();
  const trendData = getProfitTrendData();

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="performance-analytics">
      <div className="dashboard-header">
        <SectionTitle
          title="Performance Analytics"
        />
      </div>

      <StandaloneSection title={"PERFORMANCE SUMMARY"}>
        <div className="analytics-grid">
          <div className="analytics-card">
            <h3>Key Metrics</h3>
            {isLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>Loading metrics...</p>
              </div>
            ) : isError ? (
              <div className="error-message">Failed to load metrics</div>
            ) : (
              <div className="metrics-list">
                <div className="metric-item">
                  <span>Win Rate (Profitable Groups)</span>
                  <span className={`metric-value ${metrics.winRate >= 50 ? 'positive' : 'neutral'}`}>
                    {metrics.winRate.toFixed(1)}%
                  </span>
                </div>
                <div className="metric-item">
                  <span>Avg Trade Return</span>
                  <span className={`metric-value ${metrics.avgTradeReturn >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercentage(metrics.avgTradeReturn)}
                  </span>
                </div>
                <div className="metric-item">
                  <span>Fee Efficiency</span>
                  <span className={`metric-value ${metrics.feeEfficiency < 10 ? 'positive' : metrics.feeEfficiency < 20 ? 'neutral' : 'negative'}`}>
                    {metrics.feeEfficiency.toFixed(2)}%
                  </span>
                </div>
                <div className="metric-item">
                  <span>Total Realized Profit</span>
                  <span className={`metric-value ${metrics.totalProfit >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(metrics.totalProfit)}
                  </span>
                </div>
                <div className="metric-item">
                  <span>Active Trade Groups</span>
                  <span className="metric-value">{metrics.activeGroups}</span>
                </div>
                <div className="metric-item">
                  <span>Completed Trades (Measured, All-Time)</span>
                  {measuredTradesLoading ? (
                    <span className="metric-value">…</span>
                  ) : measuredTradesError || measuredCompletedTrades === null ? (
                    <span className="metric-value neutral">N/A</span>
                  ) : (
                    <span className="metric-value">{measuredCompletedTrades}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="analytics-card">
            <h3><TrendingUpIcon size={20} /> Profit Trend (30 Days)</h3>
            {profitTrendLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>Loading 30 days of profit data...</p>
              </div>
            ) : profitTrendError ? (
              <div className="error-message">Failed to load chart data</div>
            ) : (
              <ProfitTrendChart data={trendData} />
            )}
          </div>

          <div className="analytics-card">
            <h3>Product Performance (Top 10)</h3>
            {isLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>Loading product performance data...</p>
              </div>
            ) : isError ? (
              <div className="error-message">Failed to load chart data</div>
            ) : productData.length > 0 ? (
              <ProductPerformanceChart data={productData} />
            ) : (
              <div className="no-data-message">No product data available</div>
            )}
          </div>

          <div className="analytics-card">
            <h3>Strategy Comparison</h3>
            {isLoading ? (
              <div className="chart-loading">
                <div className="loading-spinner"></div>
                <p>Loading strategy comparison data...</p>
              </div>
            ) : isError ? (
              <div className="error-message">Failed to load chart data</div>
            ) : strategyData.length > 0 ? (
              <StrategyComparisonChart data={strategyData} />
            ) : (
              <div className="no-data-message">No strategy data available</div>
            )}
          </div>
        </div>
      </StandaloneSection>
      <DashboardBottomLogo />
    </div>
  );
};

export default PerformanceAnalytics;
